<!-- ===============================
     UPLOAD
     =============================== -->
<app-analysis-upload-input *ngIf="!isFileUploaded" [isDraggingFile]="isDraggingFile" [error]="error"
  (fileInputChange)="onFileInputChange($event)" (dragEnter)="onDragEnter($event)" (dragOver)="onDragOver($event)"
  (dragLeave)="onDragLeave($event)" (dropFile)="onDrop($event)"></app-analysis-upload-input>

<!-- ===============================
     CONTEÚDO PRINCIPAL
     =============================== -->
<div *ngIf="isFileUploaded">
  
<div *ngIf="baselineWarning" class="baseline-warning" role="alert">
  <h3>⚠ Sequential baseline missing — charts blocked</h3>
  <p style="white-space: pre-line">{{ baselineWarning }}</p>
  <p *ngIf="baselineMode === 'omp'">Fix: add <code>-c 1</code>.</p>
  <p *ngIf="baselineMode === 'mpi'">Fix: add <code>--ranks 1</code>.</p>
  <p *ngIf="baselineMode === 'hybrid'">Fix: include <code>1x1</code>.</p>
  <a routerLink="/guide">Why T_seq is required → Guide §3</a>
</div>

  <!-- Árvore de regiões -->
  <div *ngIf="measurements.length > 0" class="region-tree-section">
    <div class="region-tree-section__header">
      <p class="region-tree-section__eyebrow">Analysis Map</p>
      <h2 class="region-tree-section__title">Region Tree</h2>
      <p class="region-tree-section__description">
        Navigate the execution hierarchy and select the regions you want to inspect in detail.
      </p>
    </div>

    <app-region-tree [measurements]="measurements" [metricsByRegion]="metricsByRegion"
      [regionDetailsMap]="regionDetailsMap" [hybridViewMode]="hybridViewMode" (regionSelected)="onRegionSelected($event)">
    </app-region-tree>
  </div>

  <div *ngIf="isHybridAnalysis()" class="hybrid-view-toggle">
    <div>
      <p class="hybrid-view-toggle__label">Hybrid visualization</p>
      <p class="hybrid-view-toggle__description">{{ getHybridViewDescription() }}</p>
    </div>

    <div class="hybrid-view-toggle__buttons">
      <button type="button" class="btn btn-sm"
        [class.btn-primary]="hybridViewMode === 'total-cores'"
        [class.btn-outline]="hybridViewMode !== 'total-cores'"
        (click)="setHybridViewMode('total-cores')">
        Heatmap view
      </button>
      <button type="button" class="btn btn-sm"
        [class.btn-primary]="hybridViewMode === 'topology'"
        [class.btn-outline]="hybridViewMode !== 'topology'"
        (click)="setHybridViewMode('topology')">
        Topology view
      </button>
    </div>
  </div>

  <!-- ===============================
       REGIÃO ÚNICA
       =============================== -->
  <div *ngIf="selectedRegions.length === 1 && chartDefinitions.length > 0" class="heatmaps-container">
    <div class="charts-section-header">
      <p class="charts-section-header__eyebrow">Visual Analysis</p>
      <h2 class="charts-section-header__title">Performance Charts</h2>
      <p class="charts-section-header__description">
        Detailed metrics for the selected execution region.
      </p>
    </div>

    <div class="comparison-header mb-6">
      <h3 class="text-2xl font-bold text-center">
        {{ getRegionDisplayName(selectedRegions[0]) }}
      </h3>
    </div>

    <div class="chart-grid-container">
      <div *ngFor="let def of chartDefinitions; let i = index" class="chart-grid-item">
        <!-- HEADER DO GRÁFICO -->
        <div class="chart-header-controls mb-4">
          <div class="chart-toggle-group">
            <button class="btn btn-xs btn-outline" (click)="toggleSmooth(i)">
              {{ showSmoothed[i] ? "Detailed" : "Smooth" }}
            </button>

            <button *ngIf="isScalabilityChart(i)" class="btn btn-xs btn-outline" (click)="toggleScalabilityMode(i)">
              {{ showRelative[i] ? "Absolute" : "Relative" }}
            </button>

            <button *ngIf="isSensorChart(i)" class="btn btn-xs btn-outline" (click)="toggleSensorMode(i)">
              {{ showEDP[i] ? "Energy" : "EDP" }}
            </button>
          </div>

          <div class="chart-header-actions">
            <button
              type="button"
              class="chart-expand-btn"
              title="Expand chart"
              aria-label="Expand chart"
              (click)="openSingleChartExpansion(i)">
              <span aria-hidden="true">⤢</span>
            </button>

            <div class="download-dropdown">
              <details #downloadPopover class="speedup-popover">
                <summary class="chart-download-btn" title="Opções de download">
                  <span aria-hidden="true">⤓</span>
                </summary>

                <div class="download-menu-panel">
                  <p class="download-title">Export as:</p>
                  <button class="download-item"
                    (click)="prepareDownload(def.key, selectedRegions[0], 'png', downloadPopover)">
                    <span class="icon">🖼️</span> PNG Image
                  </button>
                  <button class="download-item"
                    (click)="prepareDownload(def.key, selectedRegions[0], 'pdf', downloadPopover)">
                    <span class="icon">📄</span> PDF Document
                  </button>
                  <button class="download-item"
                    (click)="prepareDownload(def.key, selectedRegions[0], 'svg', downloadPopover)">
                    <span class="icon">🧩</span> SVG Vector
                  </button>
                </div>
              </details>
            </div>

            <ng-container *ngIf="def.key === 'speedup'">
              <div class="speedup-float-window">
                <details #popover class="speedup-popover">
                  <summary class="speedup-trigger" aria-label="Open speedup scale settings">
                    <span aria-hidden="true">⚙</span>
                  </summary>

                  <div class="speedup-popover-panel">
                    <p class="speedup-popover-title">Speedup Scale</p>
                    <p class="speedup-popover-subtitle">
                      Set the max value for this region.
                    </p>

                    <input type="number" #scaleInput [value]="getCustomMaxForRegion(selectedRegions[0])"
                      class="speedup-input" />

                    <div class="speedup-actions">
                      <button type="button" class="btn btn-xs btn-primary" (click)="
                          updateSpeedupScale(
                            scaleInput.value,
                            selectedRegions[0], popover
                          )
                        ">
                        Apply
                      </button>
                      <button type="button" class="speedup-reset-btn"
                        (click)="updateSpeedupScale('', selectedRegions[0], popover)">
                        Auto
                      </button>
                    </div>
                  </div>
                </details>
              </div>
            </ng-container>

            <div class="chart-help" tabindex="0" aria-label="Chart explanation">
              <span class="chart-help-icon">?</span>
              <div class="chart-help-tooltip">
                {{ getChartTooltipText(def.key) }}
              </div>
            </div>
          </div>
        </div>

        <!-- LINHA: GRÁFICO + ESCALA -->
        <div class="chart-row-container" style="position: relative">
          <!-- GRÁFICO -->
          <div class="chart-area">
            <div class="chart-wrapper-relative">
              <div *ngIf="determineChartOption(i) as currentOption">
                <div echarts class="chart-instance" [options]="currentOption" [initOpts]="{ renderer: 'canvas' }"
                  (chartInit)="
                    onChartInit(
                      $event,
                      chartDefinitions[i].key,
                      selectedRegions[0]
                    )
                  "></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ===============================
       VÁRIAS REGIÕES
       =============================== -->
  <div *ngIf="selectedRegions.length > 1" class="p-4">
    <div class="comparison-container w-full">
      <div class="charts-section-header">
        <p class="charts-section-header__eyebrow">Visual Analysis</p>
        <h2 class="charts-section-header__title">Performance Charts</h2>
        <p class="charts-section-header__description">
          Side-by-side comparison of the regions currently selected in the tree.
        </p>
      </div>

      <div *ngFor="let comp of comparisonCharts" class="comparison-item mb-12 w-full flex flex-col items-center">
        <!-- TÍTULO DA COMPARAÇÃO -->
        <div class="w-full text-center mb-6">
          <h4 class="text-2xl font-bold text-gray-800 mb-4">
            {{ comp.title }}
          </h4>

          <div class="chart-header-controls comparison-controls">
            <div class="chart-toggle-group">
              <button class="btn btn-sm btn-outline" (click)="comp.showSmoothed = !comp.showSmoothed">
                {{ comp.showSmoothed ? "Detailed" : "Smooth" }}
              </button>

              <button *ngIf="isScalabilityChartComparison(comp.key)" class="btn btn-sm btn-outline"
                (click)="comp.showRelative = !comp.showRelative">
                {{ comp.showRelative ? "Absolute" : "Relative" }}
              </button>

              <button *ngIf="isSensorChartComparison(comp.key)" class="btn btn-sm btn-outline"
                (click)="comp.showEDP = !comp.showEDP">
                {{ comp.showEDP ? "Energy" : "EDP" }}
              </button>
            </div>

            <div class="chart-help" tabindex="0" aria-label="Chart explanation">
              <span class="chart-help-icon">?</span>
              <div class="chart-help-tooltip">
                {{ getChartTooltipText(comp.key) }}
              </div>
            </div>
          </div>
        </div>

        <!-- LINHA DE GRÁFICOS -->
        <div class="charts-row" cdkDropList cdkDropListOrientation="horizontal"
          (cdkDropListDropped)="drop($event, comp)">
          <div *ngFor="let region of comp.regionData" class="chart-wrapper" cdkDrag>

            <!-- HEADER -->
            <div class="drag-handle p-3 bg-gray-50 border-b flex items-center"
              [class.drag-handle--with-action]="comp.key === 'speedup'" cdkDragHandle>
              <span class="text-sm font-bold text-gray-600">
                {{ getRegionDisplayName(region.regionId) }}
              </span>

              <!-- Essa dive preenche o espaço vazio entre o título e os botões -->
              <div class="flex-grow"></div>

              <div class="header-controls">
                <button
                  type="button"
                  class="chart-expand-btn"
                  title="Expand chart"
                  aria-label="Expand chart"
                  (click)="openComparisonChartExpansion(comp, region)">
                  <span aria-hidden="true">⤢</span>
                </button>

                <div class="download-dropdown">
                  <details #downloadPopover class="speedup-popover">
                    <summary class="chart-download-btn" title="Download options">
                      <span aria-hidden="true">⤓</span>
                    </summary>

                    <div class="download-menu-panel">
                      <p class="download-title">Export as:</p>
                      <button class="download-item"
                        (click)="prepareDownload(comp.key, region.regionId, 'png', downloadPopover)">
                        <span class="icon">🖼️</span> PNG Image
                      </button>
                      <button class="download-item"
                        (click)="prepareDownload(comp.key, region.regionId, 'pdf', downloadPopover)">
                        <span class="icon">📄</span> PDF Document
                      </button>
                      <button class="download-item"
                        (click)="prepareDownload(comp.key, region.regionId, 'svg', downloadPopover)">
                        <span class="icon">🧩</span> SVG Vector
                      </button>
                    </div>
                  </details>
                </div>

                <ng-container *ngIf="comp.key === 'speedup'">
                  <div class="speedup-float-window">
                    <details #popover class="speedup-popover">
                      <summary class="speedup-trigger" aria-label="Open speedup scale settings">
                        <span aria-hidden="true">⚙</span>
                      </summary>

                      <div class="speedup-popover-panel">
                        <p class="speedup-popover-title">Speedup Scale</p>
                        <p class="speedup-popover-subtitle">Set the max value for this region.</p>

                        <input type="number" #scaleInputComp [value]="getCustomMaxForRegion(region.regionId)"
                          class="speedup-input" />

                        <div class="speedup-actions">
                          <button type="button" class="btn btn-xs btn-primary"
                            (click)="updateSpeedupScale(scaleInputComp.value, region.regionId, popover)">
                            Apply
                          </button>
                          <button type="button" class="speedup-reset-btn"
                            (click)="updateSpeedupScale('', region.regionId, popover)">
                            Auto
                          </button>
                        </div>
                      </div>
                    </details>
                  </div>
                </ng-container>
              </div>
            </div>

            <!-- LINHA: GRÁFICO + ESCALA -->
            <div class="chart-row-container" style="position: relative">
              <!-- GRÁFICO -->
              <div class="chart-area">
                <div class="chart-wrapper-relative">
                  <div *ngIf="
                      determineComparisonChartOption(
                        comp,
                        region
                      ) as chartOption;
                      else noComparisonChartData
                    ">
                    <div echarts class="chart-instance" [options]="chartOption"
                      [initOpts]="{ renderer: 'canvas', devicePixelRatio: 2 }" (chartInit)="
                        onChartInit($event, comp.key, region.regionId)
                      "></div>
                  </div>

                  <ng-template #noComparisonChartData>
                    <div *ngIf="
                        comp.key === 'imb' && hasNoLoadImbalanceData(region)
                      " class="no-imbalance-data">
                      No load imbalance data available for this region.
                    </div>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<div *ngIf="expandedChart" class="chart-modal-backdrop" (click)="closeExpandedChart()">
  <div class="chart-modal" (click)="$event.stopPropagation()">
    <div class="chart-modal__header">
      <div>
        <p class="chart-modal__eyebrow">Expanded view</p>
        <h3 class="chart-modal__title">{{ expandedChart.title }}</h3>
      </div>

      <div class="chart-modal__actions">
        <div class="download-dropdown">
          <details #expandedDownloadPopover class="speedup-popover">
            <summary class="chart-download-btn" title="Download options">
              <span aria-hidden="true">⤓</span>
            </summary>

            <div class="download-menu-panel">
              <p class="download-title">Export as:</p>
              <button class="download-item" (click)="prepareExpandedDownload('png', expandedDownloadPopover)">
                <span class="icon">🖼️</span> PNG Image
              </button>
              <button class="download-item" (click)="prepareExpandedDownload('pdf', expandedDownloadPopover)">
                <span class="icon">📄</span> PDF Vector
              </button>
              <button class="download-item" (click)="prepareExpandedDownload('svg', expandedDownloadPopover)">
                <span class="icon">🧩</span> SVG Vector
              </button>
            </div>
          </details>
        </div>

        <button
          type="button"
          class="chart-modal__close"
          aria-label="Close expanded chart"
          (click)="closeExpandedChart()">
          ✕
        </button>
      </div>
    </div>

    <div class="chart-modal__body">
      <div *ngIf="expandedChartOption as expandedOption" class="chart-modal__chart">
        <div
          echarts
          class="chart-instance chart-instance--expanded"
          [options]="expandedOption"
          [initOpts]="{ renderer: 'canvas', devicePixelRatio: 2 }"
          (chartInit)="onExpandedChartInit($event)"></div>
      </div>
    </div>
  </div>
</div>
